import { message } from 'antd'
import { format } from 'date-fns'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { sanitizeFilenameForRolodex } from 'features/files/utils/fileUploadUtils'
import { openDocumentViewer } from 'features/libra/document/documentViewerUrl'
import { BaseFormData, Report } from 'features/reporting/types'
import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import { useCallback, useMemo, useState } from 'react'
import { DocumentFileFormat } from 'routes/document-viewer/-searchParams'
import type { GeckoReportJSON } from 'types'
import { GeckoFileCategory, RolodexEntryType } from 'types'
import { REPORT_ENTRY_UPLOAD_DATE_FORMAT } from 'utils/constants'

interface PublishedReportEntry {
  id: string
  fileName: string
  siteId: string
  organizationId: string
  unitId: string
  inspectionIds: string[]
  componentIds: string[]
  jobIds: string[]
}

interface UsePdfManagementProps {
  metadataEntryId: string
  formData: BaseFormData
  report: Report
  onPublishedPathChange?: (path: string) => void
  downloadFilename?: string
  approval?: {
    publishBlocker: string | undefined
    fetchPublishableBlob: () => Promise<Blob | undefined>
  }
  publishedReportEntry?: PublishedReportEntry
}

interface UsePdfManagementResult {
  isPublishing: boolean
  latestDraftTimestamp?: string
  latestDraftPath?: string
  isLatestDraftUrlReady: boolean
  isLatestDraftUrlLoading: boolean
  publishLatestDraftPDF: (pdfPath?: string) => Promise<void>
  handleDownloadLatestDraft: () => Promise<void>
  handleDownloadPublishedPdf: () => Promise<void>
  handlePreviewLatestDraft: (temporaryPreviewUrl?: string) => void
  handlePreviewPublishedPdf: () => void
  forceRefresh: () => Promise<void>
}

dayjs.extend(utc)

export const usePdfManagement = ({
  metadataEntryId,
  formData,
  downloadFilename,
  onPublishedPathChange,
  approval,
  publishedReportEntry,
}: UsePdfManagementProps): UsePdfManagementResult => {
  const { rolodexUploadFile, rolodexPatch } = useRolodexEdit()
  const [isPublishing, setIsPublishing] = useState(false)

  const { fileRefs: pdfRefs, forceRefresh: forceRefreshPdfRefs } =
    useRolodexFileLS({
      entry_id: metadataEntryId,
      path: 'reportPDFDrafts/**',
    })
  const {
    results: publishedPdfUrls,
    forceRefresh: forceRefreshPublishedPdfUrls,
  } = useRolodexSignedURLs(
    formData.publishedPdfPath
      ? [
          {
            entry_id: metadataEntryId,
            path: formData.publishedPdfPath,
          },
        ]
      : []
  )

  const publishReportEntry = useCallback(
    async (blob: Blob) => {
      if (!publishedReportEntry) return

      const reportData: GeckoReportJSON = {
        visible_name: publishedReportEntry.fileName,
        file_type: 'pdf',
        upload_date: format(Date.now(), REPORT_ENTRY_UPLOAD_DATE_FORMAT),
        size: blob.size,
        units: [publishedReportEntry.unitId],
        components: publishedReportEntry.componentIds,
        inspections: publishedReportEntry.inspectionIds,
        jobs: publishedReportEntry.jobIds,
        file_category: GeckoFileCategory.GECKO_REPORT,
      }

      await rolodexPatch([
        {
          id: publishedReportEntry.id,
          type: RolodexEntryType.REPORT,
          data: reportData,
          tags: {
            site: publishedReportEntry.siteId,
            organization: publishedReportEntry.organizationId,
            unit: publishedReportEntry.unitId,
          },
        },
      ])
      await rolodexUploadFile(
        publishedReportEntry.id,
        blob,
        sanitizeFilenameForRolodex(publishedReportEntry.fileName)
      )
    },
    [publishedReportEntry, rolodexPatch, rolodexUploadFile]
  )

  const { latestDraftRef, latestDraftTimestamp } = useMemo(() => {
    const timestampRegex = /draft-(\d{8}-\d{6})\.pdf$/

    const sortedRefs = [...pdfRefs].sort((a, b) => {
      const timestampA = a.path.match(timestampRegex)?.[1]
      const timestampB = b.path.match(timestampRegex)?.[1]

      if (!timestampA || !timestampB) return 0

      return timestampB.localeCompare(timestampA)
    })

    const latestDraft = sortedRefs[0]
    if (!latestDraft)
      return { latestDraftRef: undefined, latestDraftTimestamp: undefined }

    const match = latestDraft.path.match(timestampRegex)
    if (!match)
      return { latestDraftRef: undefined, latestDraftTimestamp: undefined }

    const isoString = match[1].replace(
      /(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})/,
      '$1-$2-$3T$4:$5:$6Z'
    )
    const latestDraftTimestamp = dayjs
      .utc(isoString)
      .format('MMM DD, YYYY HH:mm:ss [UTC]')

    return { latestDraftRef: latestDraft, latestDraftTimestamp }
  }, [pdfRefs])

  const {
    url: latestDraftUrl,
    isLoading: isLatestDraftUrlLoading,
    isValidating: isLatestDraftUrlValidating,
    forceRefresh: forceRefreshLatestDraftUrl,
  } = useRolodexSignedURLs(
    latestDraftRef
      ? [
          {
            entry_id: latestDraftRef?.entry_id,
            path: latestDraftRef?.path,
          },
        ]
      : []
  )

  const handleDownloadLatestDraft = useCallback(async (): Promise<void> => {
    if (!latestDraftUrl) {
      message.error('No draft PDF available')
      return
    }

    try {
      const response = await fetch(latestDraftUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = downloadFilename
        ? `${downloadFilename}_Draft.pdf`
        : 'inspection-report-draft.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download draft PDF:', error)
      message.error('Failed to download draft PDF')
    }
  }, [latestDraftUrl, downloadFilename])

  const publishLatestDraftPDF = useCallback(
    async (pdfPath?: string): Promise<void> => {
      if (approval?.publishBlocker) {
        message.error(approval.publishBlocker)
        return
      }
      if (!latestDraftUrl) {
        message.error('No draft PDF available')
        return
      }

      setIsPublishing(true)
      try {
        const signedBlob = await approval?.fetchPublishableBlob()
        const blob = signedBlob ?? (await (await fetch(latestDraftUrl)).blob())

        const publishedPath =
          pdfPath ||
          formData.publishedPdfPath ||
          `reportPDFs/report-published.pdf`

        await rolodexUploadFile(metadataEntryId, blob, publishedPath)

        if (publishedPath !== formData.publishedPdfPath) {
          await rolodexPatch([
            {
              id: metadataEntryId,
              data: {
                published_pdf_path: publishedPath,
              },
            },
          ])
        }

        await publishReportEntry(blob)
        onPublishedPathChange?.(publishedPath)

        message.success('PDF published successfully')
      } catch (error) {
        console.error('Failed to publish draft PDF:', error)
        message.error(
          error instanceof Error ? error.message : 'Failed to publish draft PDF'
        )
      } finally {
        setIsPublishing(false)
      }
    },
    [
      approval,
      formData.publishedPdfPath,
      metadataEntryId,
      latestDraftUrl,
      publishReportEntry,
      onPublishedPathChange,
      rolodexUploadFile,
      rolodexPatch,
    ]
  )

  const handleDownloadPublishedPdf = useCallback(async (): Promise<void> => {
    if (!formData.publishedPdfPath) {
      message.error('No published PDF available')
      return
    }

    const publishedUrl = publishedPdfUrls[0]?.signed_url
    if (!publishedUrl) {
      message.error('Failed to get published PDF URL')
      return
    }

    try {
      const response = await fetch(publishedUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = downloadFilename
        ? `${downloadFilename}.pdf`
        : 'inspection-report-published.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download published PDF:', error)
      message.error('Failed to download published PDF')
    }
  }, [formData.publishedPdfPath, publishedPdfUrls, downloadFilename])

  const handlePreviewLatestDraft = useCallback(
    (temporaryPreviewUrl?: string): void => {
      const path = latestDraftRef?.path
      if (!path && !temporaryPreviewUrl) {
        message.error('No draft PDF available')
        return
      }
      openDocumentViewer({
        entryId: metadataEntryId,
        filePath: path ?? 'reportPDFDrafts/draft.pdf',
        title: downloadFilename
          ? `${downloadFilename} Draft.pdf`
          : 'Inspection report draft.pdf',
        fileFormat: DocumentFileFormat.PDF,
        temporaryPreviewUrl,
      })
    },
    [downloadFilename, latestDraftRef, metadataEntryId]
  )

  const handlePreviewPublishedPdf = useCallback((): void => {
    if (!formData.publishedPdfPath) {
      message.error('No published PDF available')
      return
    }
    openDocumentViewer({
      entryId: metadataEntryId,
      filePath: formData.publishedPdfPath,
      title: downloadFilename
        ? `${downloadFilename}.pdf`
        : 'Inspection report published.pdf',
      fileFormat: DocumentFileFormat.PDF,
    })
  }, [downloadFilename, formData.publishedPdfPath, metadataEntryId])

  const forceRefresh = useCallback(async () => {
    await forceRefreshPdfRefs()
    await forceRefreshLatestDraftUrl()
    await forceRefreshPublishedPdfUrls()
  }, [
    forceRefreshPdfRefs,
    forceRefreshLatestDraftUrl,
    forceRefreshPublishedPdfUrls,
  ])

  return {
    isPublishing,
    latestDraftTimestamp,
    latestDraftPath: latestDraftRef?.path,
    isLatestDraftUrlReady: Boolean(latestDraftUrl),
    isLatestDraftUrlLoading:
      isLatestDraftUrlLoading || isLatestDraftUrlValidating,
    publishLatestDraftPDF,
    handleDownloadLatestDraft,
    handleDownloadPublishedPdf,
    handlePreviewLatestDraft,
    handlePreviewPublishedPdf,
    forceRefresh,
  }
}
